import React, { useEffect, useMemo, useRef, useState } from "react";
import { Eye, EyeOff, Upload } from "lucide-react";
import StudentLayout from "../../layouts/StudentLayout";
import StudentHeaderSection from "../../components/student/StudentHeaderSection";
import FormInput from "../../components/common/FormInput";
import FormSelect from "../../components/common/FormSelect";
import Toast from "../../components/common/Toast";
import { LOGGED_IN_USER_UPDATED } from "../../constants/sessionEvents";
import {
  useGetSingleStudentQuery,
  useUpdateStudentInformationMutation,
  useChangePasswordMutation,
} from "../../services/user";
import { useGetAllAgeGroupsQuery } from "../../services/category";
import LogoutModal from "../../components/student/LogoutModal";

const emptyProfile = {
  parentPhoneNumber: "+92",
  phoneNumber: "+92",
  ageGroup: "",
  bio: "",
};

const INTERNATIONAL_PHONE_REGEX = /^\+[1-9]\d{7,14}$/;

const isValidInternationalPhone = (value) =>
  INTERNATIONAL_PHONE_REGEX.test(String(value).trim());

function mergeStudentProfileIntoStorage(res, formPayload) {
  const prev = JSON.parse(localStorage.getItem("loggedInUser") || "{}");
  let fromApi = {};

  const absorb = (obj) => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return;
    Object.assign(fromApi, obj);
    if (obj.student && typeof obj.student === "object") {
      Object.assign(fromApi, obj.student);
    }
    if (obj.user && typeof obj.user === "object") {
      Object.assign(fromApi, obj.user);
    }
  };

  const d = res?.data;
  if (Array.isArray(d) && d.length && typeof d[0] === "object") {
    absorb(d[0]);
  } else {
    absorb(d);
  }
  absorb(res?.user);
  absorb(res?.student);

  const next = {
    ...prev,
    ...fromApi,
    parentPhoneNumber:
      fromApi.parentPhoneNumber ??
      fromApi.parentPhone ??
      formPayload.parentPhoneNumber,
    phoneNumber:
      fromApi.phoneNumber ?? fromApi.phone ?? formPayload.phoneNumber,
    ageGroup:
      formPayload.ageGroup ??
      fromApi.ageGroup?._id ??
      fromApi.ageGroup ??
      prev.ageGroup,
    bio: fromApi.bio ?? formPayload.bio,
    profilePicture:
      fromApi.profilePicture ??
      (typeof fromApi.profilePicture === "object"
        ? fromApi.profilePicture?.secureUrl
        : null) ??
      prev.profilePicture,
  };
  localStorage.setItem("loggedInUser", JSON.stringify(next));
  window.dispatchEvent(new CustomEvent(LOGGED_IN_USER_UPDATED));
}

const Settings = () => {
  const photoInputRef = useRef(null);
  const hasHydratedProfileRef = useRef(false);
  const [avatarSrc, setAvatarSrc] = useState("");
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profile, setProfile] = useState(emptyProfile);
  const [updateStudentInformation, { isLoading: isSavingProfile }] =
    useUpdateStudentInformationMutation();
  const [changePassword, { isLoading: isChangingPassword }] =
    useChangePasswordMutation();
  const { data: studentProfileData } = useGetSingleStudentQuery();
  const { data: ageGroupsData } = useGetAllAgeGroupsQuery();

  const ageGroupOptions = useMemo(() => {
    const ageGroups = Array.isArray(ageGroupsData?.data)
      ? ageGroupsData.data
      : [];

    return ageGroups.map((ageGroup) => ({
      label: ageGroup.title,
      value: ageGroup._id,
    }));
  }, [ageGroupsData]);

  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const passwordMatchErrors = useMemo(() => {
    const { newPassword, confirmNewPassword } = passwords;

    if (!confirmNewPassword) {
      return { newPassword: "", confirmNewPassword: "" };
    }

    if (newPassword !== confirmNewPassword) {
      return {
        newPassword: "Passwords do not match",
        confirmNewPassword: "Passwords do not match",
      };
    }

    return { newPassword: "", confirmNewPassword: "" };
  }, [passwords]);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    title: "",
    message: "",
    variant: "default",
  });

  const showToast = (title, message, variant = "default") => {
    setToast({ show: true, title, message, variant });
  };

  useEffect(() => {
    const student = studentProfileData?.data;
    if (!student || hasHydratedProfileRef.current) return;

    hasHydratedProfileRef.current = true;

    setProfile({
      parentPhoneNumber: student.student?.parentPhoneNumber || "+92",
      phoneNumber: student.phoneNumber || "+92",
      ageGroup: student.student?.ageGroup?._id || "",
      bio: student.bio || "",
    });

    if (typeof student.profilePicture === "string" && student.profilePicture) {
      setAvatarSrc(student.profilePicture);
    }
  }, [studentProfileData]);

  useEffect(() => {
    return () => {
      if (typeof avatarSrc === "string" && avatarSrc.startsWith("blob:")) {
        URL.revokeObjectURL(avatarSrc);
      }
    };
  }, [avatarSrc]);

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showToast("Invalid file", "Please choose an image file.", "error");
      return;
    }
    const maxBytes = 1024 * 1024;
    if (file.size > maxBytes) {
      showToast("File too large", "Image must be under 1MB.", "error");
      return;
    }
    setProfileImageFile(file);
    setAvatarSrc(URL.createObjectURL(file));
  };

  const handleProfileField = (field) => (e) => {
    const v = e.target.value;
    setProfile((prev) => ({ ...prev, [field]: v }));
  };

  const handlePasswordField = (field) => (e) => {
    const v = e.target.value;
    setPasswords((prev) => ({ ...prev, [field]: v }));
  };

  const handleChangePasswordSubmit = async () => {
    const currentPassword = passwords.currentPassword.trim();
    const newPassword = passwords.newPassword.trim();
    const confirmNewPassword = passwords.confirmNewPassword.trim();

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      showToast(
        "Missing fields",
        "Please fill in all password fields.",
        "error",
      );
      return;
    }
    if (newPassword !== confirmNewPassword) {
      showToast(
        "Passwords do not match",
        "New password and confirmation must match.",
        "error",
      );
      return;
    }
    if (newPassword === currentPassword) {
      showToast(
        "Invalid new password",
        "New password must be different from your current password.",
        "error",
      );
      return;
    }

    const payload = {
      currentPassword,
      newPassword,
      confirmNewPassword,
    };

    try {
      const res = await changePassword(payload).unwrap();
      if (res && typeof res === "object" && res.success === false) {
        showToast(
          "Could not update password",
          typeof res.message === "string"
            ? res.message
            : "Password change failed.",
          "error",
        );
        return;
      }
      setPasswords({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });
      showToast(
        "Password updated",
        typeof res?.message === "string"
          ? res.message
          : "Your password has been changed.",
        "success",
      );
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.data?.error ||
        err?.error ||
        "Could not change password. Please try again.";
      showToast(
        "Could not update password",
        typeof msg === "string" ? msg : "Password change failed.",
        "error",
      );
    }
  };

  const handleSaveProfile = async () => {
    const parentPhoneNumber = profile.parentPhoneNumber.trim();
    const phoneNumber = profile.phoneNumber.trim();

    if (!isValidInternationalPhone(parentPhoneNumber)) {
      showToast(
        "Invalid parent phone number",
        "Parent phone number must be in international format (e.g., +923001234567).",
        "error",
      );
      return;
    }

    if (!isValidInternationalPhone(phoneNumber)) {
      showToast(
        "Invalid phone number",
        "Phone number must be in international format (e.g., +923001234567).",
        "error",
      );
      return;
    }

    const bio = profile.bio.trim();
    if (!bio) {
      showToast("Missing bio", "Bio is required.", "error");
      return;
    }
    if (bio.length < 20 || bio.length > 100) {
      showToast("Invalid bio", "Bio must be 20–100 characters.", "error");
      return;
    }

    const formPayload = {
      parentPhoneNumber,
      phoneNumber,
      ageGroup: profile.ageGroup,
      bio,
    };

    const payload = new FormData();
    payload.append("parentPhoneNumber", parentPhoneNumber);
    payload.append("phoneNumber", phoneNumber);
    payload.append("ageGroup", profile.ageGroup);
    payload.append("bio", bio);
    if (profileImageFile) {
      payload.append("profilePicture", profileImageFile);
    }

    try {
      const res = await updateStudentInformation(payload).unwrap();
      if (res && typeof res === "object" && res.success === false) {
        showToast(
          "Could not save",
          typeof res.message === "string"
            ? res.message
            : "Could not update profile.",
          "error",
        );
        return;
      }
      if (profileImageFile) {
        setProfileImageFile(null);
        const updatedPicture =
          res?.data?.profilePicture?.secureUrl ??
          res?.data?.profilePicture ??
          res?.profilePicture?.secureUrl ??
          res?.profilePicture;
        if (typeof updatedPicture === "string" && updatedPicture) {
          setAvatarSrc(updatedPicture);
        }
      }
      mergeStudentProfileIntoStorage(res, formPayload);
      showToast(
        "Profile updated",
        typeof res?.message === "string"
          ? res.message
          : "Your profile has been saved.",
        "success",
      );
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.data?.error ||
        err?.error ||
        "Update failed. Please try again.";
      showToast(
        "Could not save",
        typeof msg === "string" ? msg : "Update failed.",
        "error",
      );
    }
  };
  const handlePakistaniPhone = (field) => (e) => {
    let value = e.target.value;

    // Always keep +92
    if (!value.startsWith("+92")) {
      value = "+92" + value.replace(/\D/g, "");
    }

    // Keep only digits after +92
    let digits = value.slice(3).replace(/\D/g, "");

    // Pakistan mobile number = 10 digits after +92
    digits = digits.slice(0, 10);

    setProfile((prev) => ({
      ...prev,
      [field]: `+92${digits}`,
    }));
  };

  return (
    <StudentLayout>
      <div className="sm:block hidden">
        <StudentHeaderSection
          isBtnIcon={false}
          btnText="Logout"
          onClick={() => setShowLogoutModal(true)}
        />
      </div>

      <div className="px-6 sm:px-10 lg:px-14 xl:px-20  pt-10 sm:pt-16 xl:pt-24 pb-10 lg:pb-14 ">
        <div className="flex justify-end">
          <div className="max-w-[1396px] w-full ">
            <section>
              <h2 className="text-2xl font-semibold tracking-[-1%] text-[#1D2026]">
                Account Setting
              </h2>

              <div className="mt-8 lg:mt-10 flex flex-col sm:flex-row items-start lg:items-center gap-6 lg:gap-9 ">
                <div className="border border-[#00000080] bg-white p-6 lg:p-8 xl:p-11">
                  <input
                    ref={photoInputRef}
                    id="profile-photo-input"
                    type="file"
                    name="profilePhoto"
                    accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif"
                    className="sr-only"
                    onChange={handleProfilePhotoChange}
                  />
                  <div className="relative  mx-auto sm:h-52 lg:h-[280px] sm:w-52 lg:w-[280px] overflow-hidden bg-[#E5E7EB]">
                    {avatarSrc ? (
                      <img
                        src={avatarSrc}
                        alt="Profile"
                        className="h-full w-full object-cover"
                      />
                    ) : null}

                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      className="absolute bottom-0 left-0 right-0 flex cursor-pointer items-center justify-center gap-2 bg-black/60 py-2.5 lg:py-[14px] text-sm font-medium text-white"
                    >
                      <Upload size={16} aria-hidden />
                      Upload Photo
                    </button>
                  </div>

                  <p className="hidden sm:block mt-3 lg:mt-5 text-center text-sm leading-[22px] text-[#6E7485]">
                    Image size should be under 1MB and
                    <br />
                    image ration needs to be 1:1
                  </p>
                </div>

                <div className="w-full grid gap-3 lg:gap-5 grid-cols-1 lg:grid-cols-2">
                  <FormInput
                    id="parentPhoneNumber"
                    label="Parent phone number"
                    placeholder="+923291792332"
                    type="tel"
                    value={profile.parentPhoneNumber}
                    autoComplete="tel"
                    onChange={handlePakistaniPhone("parentPhoneNumber")}
                    className="w-full"
                    labelClassName="font-medium text-[#000]"
                    inputClassName="mt-2 text-sm rounded-none border border-[#00000080] px-6 !py-[15px] placeholder:text-[#4A4A68]"
                  />
                  <FormInput
                    id="phoneNumber"
                    label="Phone number"
                    placeholder="+923291792332"
                    type="tel"
                    value={profile.phoneNumber}
                    autoComplete="tel"
                    onChange={handlePakistaniPhone("phoneNumber")}
                    className="w-full"
                    labelClassName="font-medium text-[#000]"
                    inputClassName="mt-2 text-sm rounded-none border border-[#00000080] px-6 !py-[15px] placeholder:text-[#4A4A68]"
                  />

                  <FormSelect
                    id="ageGroup"
                    label="Age Group"
                    options={ageGroupOptions}
                    placeholder="Select age group"
                    defaultValue={profile.ageGroup}
                    onChange={(value) =>
                      setProfile((prev) => ({ ...prev, ageGroup: value }))
                    }
                    className="w-full"
                    labelClassName="font-medium text-[#000]"
                    selectClassName="!mt-0 !h-auto rounded-none border border-[#00000080] px-6 !py-[15px] text-sm"
                  />

                  <div className="lg:col-span-2">
                    <label className="font-medium text-[#000]" htmlFor="bio">
                      Bio
                    </label>
                    <textarea
                      id="bio"
                      placeholder="Short biography"
                      maxLength={100}
                      className="mt-2 w-full rounded-none border border-[#00000080] px-6 py-3 text-sm text-black outline-none resize-none placeholder:text-[#4A4A68]"
                      rows={4}
                      value={profile.bio}
                      onChange={handleProfileField("bio")}
                    />
                  </div>

                  <div className="lg:col-span-2 flex sm:justify-end pt-4">
                    <button
                      type="button"
                      onClick={handleSaveProfile}
                      disabled={isSavingProfile}
                      className="h-10 sm:h-[56px] tracking-[-1%] bg-[#FF6636] px-16 text-base sm:text-lg font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isSavingProfile ? "Saving…" : "Save Changes"}
                    </button>
                  </div>
                </div>
              </div>
            </section>
            <div>
              <section className="mt-12 max-w-[991px] w-full">
                <h2 className="text-2xl font-semibold tracking-[-1%] text-[#1D2026]">
                  Change Password
                </h2>

                <div className="mt-6 sm:mt-10 lg:mt-12 space-y-5">
                  <FormInput
                    id="currentPassword"
                    label="Current Password"
                    type={showCurrentPassword ? "text" : "password"}
                    placeholder="Current Password"
                    autoComplete="current-password"
                    value={passwords.currentPassword}
                    
                    onChange={handlePasswordField("currentPassword")}
                    className="w-full"
                    labelClassName="font-medium text-[#000]"
                    inputClassName="mt-2 rounded-none border border-[#00000080] px-6 !py-[13px] placeholder:text-[#938A96] pr-12 disabled:cursor-not-allowed disabled:bg-[#F5F5F5] disabled:text-[#938A96]"
                    rightContent={
                      <button
                        type="button"
                        onClick={() =>
                          setShowCurrentPassword((current) => !current)
                        }
                        className="pointer-events-auto text-[#938A96]"
                      >
                        {showCurrentPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    }
                  />

                  <FormInput
                    id="newPassword"
                    label="New Password"
                    type={showNewPassword ? "text" : "password"}
                    placeholder="New Password"
                    autoComplete="new-password"
                    value={passwords.newPassword}
                    onChange={handlePasswordField("newPassword")}
                    errorMessage={passwordMatchErrors.newPassword}
                    className="w-full"
                    labelClassName="font-medium text-[#000]"
                    inputClassName="mt-2 rounded-none border border-[#00000080] px-6 !py-[13px] placeholder:text-[#938A96] pr-12"
                    rightContent={
                      <button
                        type="button"
                        onClick={() =>
                          setShowNewPassword((current) => !current)
                        }
                        className="pointer-events-auto text-[#938A96]"
                      >
                        {showNewPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    }
                  />

                  <FormInput
                    id="confirmNewPassword"
                    label="Confirm New Password"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm New Password"
                    autoComplete="new-password"
                    value={passwords.confirmNewPassword}
                    onChange={handlePasswordField("confirmNewPassword")}
                    errorMessage={passwordMatchErrors.confirmNewPassword}
                    className="w-full"
                    labelClassName="font-medium text-[#000]"
                    inputClassName="mt-2 rounded-none border border-[#00000080] px-6 !py-[13px] placeholder:text-[#938A96] pr-12"
                    rightContent={
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword((current) => !current)
                        }
                        className="pointer-events-auto text-[#938A96]"
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    }
                  />
                </div>
                <button
                  type="button"
                  onClick={handleChangePasswordSubmit}
                  disabled={isChangingPassword}
                  className="mt-10 h-10 sm:h-[56px] px-16 bg-[#FF6636] text-base sm:text-lg font-semibold text-white tracking-[-1%] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isChangingPassword ? "Updating…" : "Change Password"}
                </button>
              </section>
            </div>
          </div>
        </div>
      </div>

      {showLogoutModal && (
        <LogoutModal setShowLogoutModal={setShowLogoutModal} />
      )}

      {toast.show ? (
        <Toast
          variant={toast.variant}
          title={toast.title}
          message={toast.message}
          duration={5000}
          onClose={() => setToast((t) => ({ ...t, show: false }))}
        />
      ) : null}
    </StudentLayout>
  );
};

export default Settings;
